import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  TextField,
  MenuItem,
  Menu,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
// Use the ECM app's MSAL token-refreshing client (acquireTokenSilent per request) instead of
// the static-token ../api/axios. The graduates endpoints strictly verify the JWT (signature+exp),
// so a stale sessionStorage access_token 401s and the dashboard silently shows 0. apiClient always
// sends a fresh, correctly-scoped token — the same one the campaign endpoints use successfully.
import { apiClient as axios } from '../services/ecm-api';
import { getCurrentUser } from '../auth/LoginButton';
import { getUserRoles } from '../rbac/roles';

// 'expired-written-off' is set when a graduate received the full three-email sequence and their
// DocuSign envelope then expired. They are closed: no signed release is required and nothing
// further is sent. It is deliberately NOT 'completed' - they did not complete - and deliberately
// not hidden, so the decision stays visible rather than being inferred from an absence.
type GraduateStatus = 'pending' | 'sent' | 'completed' | 'failed' | 'expired-written-off';
const STATUS_OPTIONS: GraduateStatus[] = ['pending', 'sent', 'completed', 'failed', 'expired-written-off'];
// Mirrors GRAD_MAX_EMAILS in function_app.py. The API is the real gate; this only decides
// whether to ask for a reason before making the request.
const GRAD_MAX_EMAILS = 3;

interface Graduate {
  id: string;
  studentId: string;
  name: string;
  email: string;
  graduationDate: string;
  program: string;
  degreeLevel: string;
  surveyScheduledDate: string;
  surveyStatus: GraduateStatus;
  surveySubmittedAt?: string;
  // Set by Relabel release-only (M5): what the status was, and why it was corrected.
  surveyStatusCorrectedFrom?: string;
  surveyStatusCorrectedAt?: string;
  surveyStatusCorrectedReason?: string;
  surveyStatusCorrectedBy?: string;
  docusignStatus: string;
  docusignSignedAt?: string;
  signedDocumentUrl?: string;
  gradEmailHistory?: { attempt: number; stage: string; date: string; at?: string }[];
  gradEmailCount?: number;
  lastGradEmailDate?: string;
  surveyResendCount?: number;
  resendHistory?: {
    at: string;
    by: string;
    justification: string;
    campaignId?: string;
    previousStatus?: string;
    capOverride?: boolean;
    emailCountAtSend?: number;
  }[];
  createdAt: string;
  updatedAt: string;
}

// The counts POST /graduates/relabel-release-only answers with (a dry run or the real thing).
interface RelabelResult {
  dryRun: boolean;
  checked: number;
  releaseOnly: number;
  toRelabel: number;
  relabelled: number;
  changedMeanwhile: number;
  failed: number;
  kept: {
    relabelledBefore: number;
    responseLinked: number;
    responseUnlinkedSameEmail: number;
    responseViaDeletedRecord: number;
    neverEmailed: number;
  };
  siblingHasResponse: number;
  reminders: {
    enabled: boolean;
    eligible: number;
    // Each record's first reminder; emails counts every reminder if none of them answers.
    byDate: Record<string, number>;
    byStage: Record<string, number>;
    emails: number;
    wouldFlagAnomaly: number;
    noneWithinAYear: number;
    notEligible: Record<string, number>;
  };
  message: string;
}

export default function GraduateSurveys({ currentTheme }: { currentTheme?: string }) {
  // Graduate.Viewer is read-only: it can view the list/counts/details but not add, sync, send,
  // edit, or delete. Graduate.Admin / Survey.Admin / Campaign.Manager get full management.
  const canManage = getUserRoles(getCurrentUser()).some(r =>
    ['Survey.Admin', 'Campaign.Manager', 'Graduate.Admin'].includes(r));
  // Relabel release-only is a one-time correction of placement records: Survey.Admin only, as the
  // API enforces.
  const isSurveyAdmin = getUserRoles(getCurrentUser()).includes('Survey.Admin');
  const [graduates, setGraduates] = useState<Graduate[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [docusignFilter, setDocusignFilter] = useState<string>('all');
  const [programFilter, setProgramFilter] = useState<string>('all');
  const [nameFilter, setNameFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [programs, setPrograms] = useState<string[]>([]);
  const [detailDialog, setDetailDialog] = useState<{ open: boolean; graduate: Graduate | null }>({
    open: false,
    graduate: null,
  });
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; graduate: Graduate | null }>({
    open: false,
    graduate: null,
  });
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [statusMenu, setStatusMenu] = useState<{ anchor: HTMLElement | null; graduate: Graduate | null }>({ anchor: null, graduate: null });
  const [bulkStatusAnchor, setBulkStatusAnchor] = useState<HTMLElement | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingDocuSign, setSyncingDocuSign] = useState(false);
  // null = closed; the dialog only ever opens on a dry run's counts.
  const [relabelPreview, setRelabelPreview] = useState<RelabelResult | null>(null);
  const [relabelBusy, setRelabelBusy] = useState(false);
  const [sendingSurvey, setSendingSurvey] = useState<string | null>(null);
  const [resendDialog, setResendDialog] = useState<{
    open: boolean;
    graduate: Graduate | null;
    justification: string;
    error: string;
  }>({ open: false, graduate: null, justification: '', error: '' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });
  const [newGraduate, setNewGraduate] = useState({
    studentId: '',
    name: '',
    email: '',
    graduationDate: '',
    program: '',
    degreeLevel: 'Associate',
    timezone: 'America/New_York',
  });

  useEffect(() => {
    loadGraduates();
  }, [statusFilter, docusignFilter, programFilter, dateRange]);

  const loadGraduates = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params: any = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (docusignFilter && docusignFilter !== 'all') params.docusignStatus = docusignFilter;
      if (programFilter && programFilter !== 'all') params.program = programFilter;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;

      const response = await axios.get('/graduates', { params });
      const graduateData = response.data.graduates || [];

      setGraduates(graduateData);

      // Extract unique programs for filter
      const uniquePrograms = [...new Set(graduateData.map((g: Graduate) => g.program))];
      setPrograms(uniquePrograms as string[]);

      setLoading(false);
    } catch (error: any) {
      console.error('Error loading graduates:', error);
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (graduate: Graduate) => {
    if (!graduate.signedDocumentUrl) {
      alert('Signed document not available yet');
      return;
    }

    try {
      // Open signed document in new tab
      window.open(graduate.signedDocumentUrl, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const handleViewDetails = (graduate: Graduate) => {
    setDetailDialog({ open: true, graduate });
  };

  const handleAddGraduate = async () => {
    try {
      // Validate required fields
      if (!newGraduate.studentId || !newGraduate.name || !newGraduate.email || !newGraduate.graduationDate || !newGraduate.program) {
        alert('Please fill in all required fields');
        return;
      }

      // Ensure graduationDate is in YYYY-MM-DD format without timezone issues
      // Strip any time component and ensure it's treated as a local date
      const graduationDateOnly = newGraduate.graduationDate.split('T')[0];

      await axios.post('/graduates', {
        ...newGraduate,
        graduationDate: graduationDateOnly
      });
      
      // Reset form and close dialog
      setNewGraduate({
        studentId: '',
        name: '',
        email: '',
        graduationDate: '',
        program: '',
        degreeLevel: 'Associate',
        timezone: 'America/New_York',
      });
      setAddDialog(false);
      
      // Reload graduates
      loadGraduates();
      alert('Graduate added successfully!');
    } catch (error: any) {
      console.error('Error adding graduate:', error);
      alert(error.response?.data?.detail || 'Failed to add graduate');
    }
  };

  const handleEditGraduate = async () => {
    if (!editDialog.graduate) return;
    
    try {
      // Ensure graduationDate is in YYYY-MM-DD format without timezone issues
      const graduationDateOnly = editDialog.graduate.graduationDate.split('T')[0];
      
      await axios.put(`/graduates/${editDialog.graduate.id}`, {
        ...editDialog.graduate,
        graduationDate: graduationDateOnly
      });
      
      setEditDialog({ open: false, graduate: null });
      loadGraduates();
      alert('Graduate updated successfully!');
    } catch (error: any) {
      console.error('Error updating graduate:', error);
      alert(error.response?.data?.detail || 'Failed to update graduate');
    }
  };

  const handlePreviewSurvey = () => {
    // Open the graduate placement survey in a new window
    window.open('https://surveys.lauruscollege.edu/graduate-placement', '_blank');
  };

  const closeResendDialog = () =>
    setResendDialog({ open: false, graduate: null, justification: '', error: '' });

  const handleSendSurvey = async (graduate: Graduate, justification?: string) => {
    setSendingSurvey(graduate.id);
    try {
      await axios.post(
        `/graduates/${graduate.id}/send-survey`,
        justification ? { justification } : {},
      );
      setSnackbar({
        open: true,
        message: `Survey queued for ${graduate.name} \u2014 email will arrive within a minute`,
        severity: 'success',
      });
      closeResendDialog();
      await loadGraduates();
    } catch (error: any) {
      // 422 is the API asking for a reason. Re-open the dialog with the message in place rather
      // than a snackbar that vanishes before it can be acted on.
      if (error.response?.status === 422) {
        setResendDialog({
          open: true,
          graduate,
          justification: justification ?? '',
          error: error.response?.data?.error || 'A justification is required.',
        });
      } else {
        const msg = error.response?.data?.error || 'Failed to send survey';
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setSendingSurvey(null);
    }
  };

  // Every graduate gets a send button. Whether a reason is needed first is decided here for
  // convenience and enforced by the API, which is the actual gate.
  // A repeat resend needs a reason, and so does sending to someone who has already had their
  // full allowance of emails.
  const needsJustification = (g: Graduate) =>
    (g.surveyResendCount ?? 0) >= 1 || (g.gradEmailCount ?? 0) >= GRAD_MAX_EMAILS;

  const handleSendClick = (graduate: Graduate) => {
    if (needsJustification(graduate)) {
      setResendDialog({ open: true, graduate, justification: '', error: '' });
    } else {
      handleSendSurvey(graduate);
    }
  };

  const handleChangeStatus = async (graduate: Graduate, newStatus: GraduateStatus) => {
    setStatusMenu({ anchor: null, graduate: null });
    if (newStatus === graduate.surveyStatus) return;
    try {
      await axios.put(`/graduates/${graduate.id}`, { surveyStatus: newStatus });
      setSnackbar({ open: true, message: `Status set to "${newStatus}" for ${graduate.name}`, severity: 'success' });
      await loadGraduates();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update status', severity: 'error' });
    }
  };

  const handleBulkChangeStatus = async (newStatus: GraduateStatus) => {
    setBulkStatusAnchor(null);
    if (selectedIds.length === 0) return;
    try {
      setUpdatingStatus(true);
      await Promise.all(selectedIds.map((id) => axios.put(`/graduates/${id}`, { surveyStatus: newStatus })));
      setSnackbar({ open: true, message: `Set ${selectedIds.length} graduate(s) to "${newStatus}"`, severity: 'success' });
      setSelectedIds([]);
      await loadGraduates();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update statuses', severity: 'error' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    try {
      setBulkDeleting(true);
      
      // Delete each selected graduate
      const deletePromises = selectedIds.map(id => 
        axios.delete(`/graduates/${id}`)
      );
      
      await Promise.all(deletePromises);
      
      // Clear selection and close dialog
      setSelectedIds([]);
      setBulkDeleteDialog(false);
      
      // Reload graduates
      await loadGraduates();
      
      alert(`Successfully deleted ${selectedIds.length} graduate(s)`);
    } catch (error: any) {
      console.error('Error deleting graduates:', error);
      alert(error.response?.data?.detail || 'Failed to delete graduates');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSyncGraduates = async () => {
    try {
      setSyncing(true);
      setSnackbar({ open: true, message: 'Checking SIS integration...', severity: 'info' });
      
      const response = await axios.post('/sync-graduates');
      
      if (response.data.success) {
        const { new_count, updated_count, skipped_count, total_processed } = response.data;
        setSnackbar({
          open: true,
          message: `Sync complete: ${new_count} new, ${updated_count} updated, ${skipped_count} skipped (${total_processed} total)`,
          severity: 'success'
        });
        
        // Reload graduates to show changes
        await loadGraduates();
      } else {
        // SIS not enabled or configuration issue
        setSnackbar({
          open: true,
          message: response.data.message || 'SIS integration not configured',
          severity: 'info'
        });
      }
    } catch (error: any) {
      console.error('Error syncing graduates:', error);
      
      // Handle 503 Service Unavailable (SIS disabled) as info, not error
      if (error.response?.status === 503) {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'SIS integration is currently disabled. Graduates are managed manually.',
          severity: 'info'
        });
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Failed to sync graduates',
          severity: 'error'
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncDocuSign = async () => {
    try {
      setSyncingDocuSign(true);
      setSnackbar({ open: true, message: 'Syncing DocuSign statuses from production...', severity: 'info' });
      
      const response = await axios.post('/sync-docusign');
      
      if (response.data.success) {
        // 'completed' counts signed PDFs newly filed. It never meant surveys completed, and since M5 a
        // signature does not complete the survey at all, so it is labelled for what it is.
        const { checked, updated, completed, failed } = response.data;
        setSnackbar({
          open: true,
          message: `DocuSign sync complete: ${checked} checked, ${updated} updated, ${completed} signed form(s) newly archived${failed > 0 ? `, ${failed} failed` : ''}`,
          severity: 'success'
        });
        
        // Reload graduates to show updated statuses
        await loadGraduates();
      }
    } catch (error: any) {
      console.error('Error syncing DocuSign:', error);
      
      if (error.response?.status === 503) {
        setSnackbar({
          open: true,
          message: 'DocuSign is not configured',
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || 'Failed to sync DocuSign statuses',
          severity: 'error'
        });
      }
    } finally {
      setSyncingDocuSign(false);
    }
  };

  // Relabel release-only (M5): always a dry run first, whose counts the confirm dialog shows. Only its
  // confirm button sends dryRun: false, the one body the API writes on.
  const handleRelabelPreview = async () => {
    try {
      setRelabelBusy(true);
      const response = await axios.post('/graduates/relabel-release-only', { dryRun: true });
      setRelabelPreview(response.data as RelabelResult);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Could not count the release-only records',
        severity: 'error',
      });
    } finally {
      setRelabelBusy(false);
    }
  };

  const handleRelabelConfirm = async () => {
    try {
      setRelabelBusy(true);
      const response = await axios.post('/graduates/relabel-release-only', { dryRun: false });
      const result = response.data as RelabelResult;
      setRelabelPreview(null);
      setSnackbar({
        open: true,
        message: result.message,
        severity: result.failed > 0 ? 'error' : 'success',
      });
      await loadGraduates();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Relabel failed. Refresh, then run it again: it only changes records that are still release-only.',
        severity: 'error',
      });
    } finally {
      setRelabelBusy(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'sent':
        return 'info';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDocuSignStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'sent':
      case 'delivered':
        return 'info';
      case 'not_sent':
        return 'default';
      case 'declined':
      case 'voided':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'studentId',
      headerName: 'ID',
      flex: 0.6,
      minWidth: 90,
    },
    {
      field: 'program',
      headerName: 'Program',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'graduationDate',
      headerName: 'Graduated',
      width: 100,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const [year, month, day] = params.value.split('T')[0].split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      },
    },
    {
      field: 'surveyStatus',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const getStatusStyle = (status: string) => {
          switch (status) {
            case 'completed':
              return { backgroundColor: 'var(--success)', color: '#ffffff' };
            case 'sent':
              return { backgroundColor: 'var(--accent)', color: 'var(--button-text)' };
            case 'pending':
              return { backgroundColor: 'var(--warning)', color: '#000000' };
            case 'failed':
              return { backgroundColor: 'var(--error)', color: '#ffffff' };
            default:
              return { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)' };
          }
        };
        
        // Read-only roles (Graduate.Viewer) get a static status chip — no click-to-change.
        return canManage ? (
          <Tooltip title="Click to change status">
            <Chip
              label={params.value || 'Unknown'}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setStatusMenu({ anchor: e.currentTarget, graduate: params.row });
              }}
              sx={{ cursor: 'pointer', ...getStatusStyle(params.value) }}
            />
          </Tooltip>
        ) : (
          <Chip
            label={params.value || 'Unknown'}
            size="small"
            sx={{ ...getStatusStyle(params.value) }}
          />
        );
      },
    },
    {
      field: 'docusignStatus',
      headerName: 'DocuSign',
      width: 110,
      renderCell: (params) => {
        const getDocuSignStyle = (status: string) => {
          switch (status) {
            case 'completed':
              return { backgroundColor: 'var(--success)', color: '#ffffff' };
            case 'sent':
            case 'delivered':
              return { backgroundColor: 'var(--accent)', color: 'var(--button-text)' };
            case 'not_sent':
              return { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)' };
            case 'declined':
            case 'voided':
              return { backgroundColor: 'var(--error)', color: '#ffffff' };
            default:
              return { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)' };
          }
        };
        
        return (
          <Chip
            label={(params.value || 'Not Sent').replace('_', ' ')}
            size="small"
            sx={{ fontSize: '0.7rem', ...getDocuSignStyle(params.value) }}
          />
        );
      },
    },
    {
      field: 'surveyResendCount',
      headerName: 'No. of Surveys Resent',
      width: 170,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const count = Number(params.value ?? 0);
        const history = params.row.resendHistory ?? [];
        const last = history[history.length - 1];
        const title =
          count === 0
            ? 'Never resent'
            : `Last resent ${last?.at ? new Date(last.at).toLocaleDateString() : 'unknown date'}` +
              ` by ${last?.by ?? 'unknown'}` +
              (last?.justification ? ` \u2014 \u201c${last.justification}\u201d` : '');
        return (
          <Tooltip title={title}>
            <Chip
              label={count}
              size="small"
              sx={{
                minWidth: 36,
                fontSize: '0.7rem',
                backgroundColor: count === 0 ? 'var(--bg-secondary)' : 'var(--warning)',
                color: count === 0 ? 'var(--text-primary)' : 'var(--button-text)',
                border: count === 0 ? '1px solid var(--card-border)' : 'none',
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 195,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canManage && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => setEditDialog({ open: true, graduate: params.row })}
              sx={{ color: 'var(--accent)' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          )}
          <Tooltip title="Preview">
            <IconButton
              size="small"
              onClick={handlePreviewSurvey}
              sx={{ color: 'var(--accent)' }}
            >
              <PreviewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Details">
            <IconButton
              size="small"
              onClick={() => handleViewDetails(params.row)}
              sx={{ color: 'var(--accent)' }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Shown for every graduate, not just 'pending' ones: the reason to resend is
              usually that the first email never arrived. */}
          {canManage && (
            <Tooltip
              title={
                needsJustification(params.row)
                  ? 'Resend survey (justification required)'
                  : params.row.surveyStatus === 'pending'
                    ? 'Send survey'
                    : 'Resend survey'
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleSendClick(params.row)}
                  disabled={sendingSurvey === params.row.id}
                  sx={{ color: 'var(--warning)' }}
                >
                  {sendingSurvey === params.row.id
                    ? <CircularProgress size={16} sx={{ color: 'var(--warning)' }} />
                    : <SendIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {params.row.signedDocumentUrl && (
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={() => handleDownloadDocument(params.row)}
                sx={{ color: 'var(--success)' }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const getStats = () => {
    const total = graduates.length;
    const pending = graduates.filter((g) => g.surveyStatus === 'pending').length;
    const sent = graduates.filter((g) => g.surveyStatus === 'sent').length;
    const completed = graduates.filter((g) => g.surveyStatus === 'completed').length;
    const signed = graduates.filter((g) => g.docusignStatus === 'completed').length;
    const writtenOff = graduates.filter((g) => g.surveyStatus === 'expired-written-off').length;

    return { total, pending, sent, completed, signed, writtenOff };
  };

  const stats = getStats();

  return (
    <Box sx={{ 
      p: 3,
      color: 'var(--text-primary)'
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{
            color: 'var(--text-primary)'
          }}>
            Graduate Placement
          </Typography>
          {selectedIds.length > 0 && (
            <Typography variant="body2" sx={{ mt: 0.5, color: 'var(--accent)' }}>
              {selectedIds.length} graduate(s) selected
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              onClick={(e) => setBulkStatusAnchor(e.currentTarget)}
              disabled={updatingStatus}
              sx={{ mr: 1, backgroundColor: 'var(--accent)', color: 'var(--button-text)', '&:hover': { backgroundColor: 'var(--accent-hover)' } }}
            >
              {updatingStatus ? 'Updating...' : `Change Status (${selectedIds.length})`}
            </Button>
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              onClick={() => setBulkDeleteDialog(true)}
              sx={{ mr: 1, backgroundColor: 'var(--error)', color: '#ffffff', '&:hover': { backgroundColor: 'var(--error)', opacity: 0.9 } }}
            >
              Delete Selected ({selectedIds.length})
            </Button>
          )}
          {canManage && (<>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialog(true)}
          >
            Add Graduate
          </Button>
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : <SyncIcon />}
            onClick={handleSyncGraduates}
            disabled={syncing}
            sx={{ backgroundColor: 'var(--accent)', color: 'var(--button-text)', '&:hover': { backgroundColor: 'var(--accent-hover)' } }}
          >
            {syncing ? 'Syncing SIS...' : 'Sync SIS'}
          </Button>
          <Button
            variant="contained"
            startIcon={syncingDocuSign ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : <SyncIcon />}
            onClick={handleSyncDocuSign}
            disabled={syncingDocuSign}
            sx={{ backgroundColor: 'var(--success)', color: '#ffffff', '&:hover': { backgroundColor: 'var(--success)', opacity: 0.9 } }}
          >
            {syncingDocuSign ? 'Syncing DS...' : 'Sync DocuSign'}
          </Button>
          {isSurveyAdmin && (
            <Tooltip title="Return graduates marked Completed only because they signed the release form to Sent. Shows the counts first.">
              <span>
                <Button
                  variant="outlined"
                  onClick={handleRelabelPreview}
                  disabled={relabelBusy}
                  startIcon={relabelBusy && !relabelPreview ? <CircularProgress size={18} /> : undefined}
                >
                  Relabel release-only
                </Button>
              </span>
            </Tooltip>
          )}
          </>)}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadGraduates}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ color: 'var(--accent)' }}>
            {stats.total}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
            Total Graduates
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ color: 'var(--warning)' }}>
            {stats.pending}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
            Pending
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ color: 'var(--accent)' }}>
            {stats.sent}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
            Sent
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ color: 'var(--success)' }}>
            {stats.completed}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
            Completed
          </Typography>
        </Paper>
        {/* The tiles are the first thing anyone reads, so they are also the quickest way to act:
            clicking one applies the matching filter instead of leaving the reader to find it. */}
        <Paper
          onClick={() => { setDocusignFilter('completed'); setStatusFilter('all'); }}
          sx={{ p: 2, textAlign: 'center', cursor: 'pointer',
                outline: docusignFilter === 'completed' ? '2px solid var(--accent)' : 'none',
                '&:hover': { backgroundColor: 'var(--bg-secondary)' } }}
        >
          <Typography variant="h4" sx={{ color: 'var(--success)' }}>
            {stats.signed}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
            Signed
          </Typography>
        </Paper>
        {/* Closed cases. Shown even at zero would be noise, so the tile appears only when there
            are some - but when there are, they must be visible rather than silently absent from
            every other total on this row. */}
        {stats.writtenOff > 0 && (
          <Paper
            onClick={() => { setStatusFilter('expired-written-off'); setDocusignFilter('all'); }}
            sx={{ p: 2, textAlign: 'center', cursor: 'pointer',
                  outline: statusFilter === 'expired-written-off' ? '2px solid var(--accent)' : 'none',
                  '&:hover': { backgroundColor: 'var(--bg-secondary)' } }}
          >
            <Typography variant="h4" sx={{ color: 'var(--text-secondary)' }}>
              {stats.writtenOff}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
              Written off
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <TextField
            select
            label="Survey Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="expired-written-off">Written off</MenuItem>
          </TextField>

          {/* Signing state is a separate axis from survey state: a graduate can be 'sent' on the
              survey and 'completed' on DocuSign. Without this the only way to find who had signed
              was to sort a column and scroll 625 rows. */}
          <TextField
            select
            label="DocuSign"
            value={docusignFilter}
            onChange={(e) => setDocusignFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="completed">Signed</MenuItem>
            <MenuItem value="sent">Awaiting signature</MenuItem>
            <MenuItem value="delivered">Opened, not signed</MenuItem>
            <MenuItem value="voided">Expired / voided</MenuItem>
            <MenuItem value="not_sent">Never sent</MenuItem>
          </TextField>

          <TextField
            select
            label="Program"
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">All Programs</MenuItem>
            {programs.map((program) => (
              <MenuItem key={program} value={program}>
                {program}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Search by Name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            size="small"
            placeholder="Type graduate name..."
          />

          <TextField
            type="date"
            label="Start Date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            type="date"
            label="End Date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      {/* Quick Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Search graduates"
          placeholder="Search by name, student ID, program, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Data Grid */}
      <Paper sx={{
        height: 600,
        width: '100%',
        backgroundColor: 'var(--card-bg)',
        borderColor: 'var(--card-border)',
        border: '1px solid var(--card-border)',
        padding: 0,
        overflow: 'hidden',
        '& .MuiDataGrid-root': {
          color: 'var(--text-primary)',
          border: 'none'
        },
        '& .MuiDataGrid-cell': {
          borderColor: 'var(--card-border)',
          color: 'var(--text-primary)'
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
          borderColor: 'var(--card-border)'
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          color: 'var(--text-primary)',
          fontWeight: 600
        },
        '& .MuiDataGrid-columnSeparator': {
          color: 'var(--card-border)'
        },
        '& .MuiDataGrid-menuIcon': {
          color: 'var(--text-primary)'
        },
        '& .MuiDataGrid-sortIcon': {
          color: 'var(--text-primary)'
        },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--card-border)',
          color: 'var(--text-primary)'
        },
        '& .MuiTablePagination-root': {
          color: 'var(--text-primary)'
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          color: 'var(--text-primary)'
        },
        '& .MuiTablePagination-select': {
          color: 'var(--text-primary)'
        },
        '& .MuiTablePagination-actions button': {
          color: 'var(--text-primary)'
        },
        '& .MuiCheckbox-root': {
          color: 'var(--accent)'
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: 'var(--bg-secondary)'
        }
      }}>
        <DataGrid
          rows={graduates.filter(g => {
            const nameOk = !nameFilter || g.name?.toLowerCase().includes(nameFilter.toLowerCase());
            const q = searchQuery.trim().toLowerCase();
            const searchOk = !q || [g.name, g.studentId, g.program, g.email]
              .some(v => v?.toLowerCase().includes(q));
            return nameOk && searchOk;
          })}
          columns={columns}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: 'surveyScheduledDate', sort: 'desc' }] },
          }}
          checkboxSelection={canManage}
          disableRowSelectionOnClick
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={(newSelection) => {
            setSelectedIds(newSelection as string[]);
          }}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, graduate: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Graduate Details</DialogTitle>
        <DialogContent>
          {detailDialog.graduate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {detailDialog.graduate.name}
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Student ID
                  </Typography>
                  <Typography>{detailDialog.graduate.studentId}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>{detailDialog.graduate.email}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Program
                  </Typography>
                  <Typography>{detailDialog.graduate.program}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Degree Level
                  </Typography>
                  <Typography>{detailDialog.graduate.degreeLevel}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Graduation Date
                  </Typography>
                  <Typography>
                    {(() => {
                      const [year, month, day] = detailDialog.graduate.graduationDate.split('T')[0].split('-');
                      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      return date.toLocaleDateString();
                    })()}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Survey Due Date
                  </Typography>
                  <Typography>
                    {(() => {
                      const [year, month, day] = detailDialog.graduate.surveyScheduledDate.split('T')[0].split('-');
                      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      return date.toLocaleDateString();
                    })()}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Survey Status
                  </Typography>
                  <Chip
                    label={detailDialog.graduate.surveyStatus}
                    size="small"
                    sx={(() => {
                      const status = detailDialog.graduate.surveyStatus;
                      switch (status) {
                        case 'completed':
                          return { backgroundColor: 'var(--success)', color: '#ffffff' };
                        case 'sent':
                          return { backgroundColor: 'var(--accent)', color: 'var(--button-text)' };
                        case 'pending':
                          return { backgroundColor: 'var(--warning)', color: '#000000' };
                        case 'failed':
                          return { backgroundColor: 'var(--error)', color: '#ffffff' };
                        default:
                          return { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)' };
                      }
                    })()}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    DocuSign Status
                  </Typography>
                  <Chip
                    label={detailDialog.graduate.docusignStatus.replace('_', ' ').toUpperCase()}
                    color={getDocuSignStatusColor(detailDialog.graduate.docusignStatus)}
                    size="small"
                  />
                </Box>

                {detailDialog.graduate.surveySubmittedAt && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Survey Submitted
                    </Typography>
                    <Typography>
                      {new Date(detailDialog.graduate.surveySubmittedAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}

                {detailDialog.graduate.docusignSignedAt && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Document Signed
                    </Typography>
                    <Typography>
                      {new Date(detailDialog.graduate.docusignSignedAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}

                {/* Kept on the record by Relabel release-only, so the correction is visible here
                    rather than looking like a survey that was never touched. */}
                {detailDialog.graduate.surveyStatusCorrectedFrom && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status Corrected
                    </Typography>
                    <Typography variant="body2">
                      {`Was "${detailDialog.graduate.surveyStatusCorrectedFrom}"`}
                      {detailDialog.graduate.surveyStatusCorrectedReason === 'release-only'
                        ? ' only because the release form was signed; no survey response was on record'
                          + ' when it was corrected.'
                        : '.'}
                      {detailDialog.graduate.surveyStatusCorrectedAt
                        ? ` Corrected ${new Date(detailDialog.graduate.surveyStatusCorrectedAt).toLocaleString()}`
                          + (detailDialog.graduate.surveyStatusCorrectedBy
                            ? ` by ${detailDialog.graduate.surveyStatusCorrectedBy}.` : '.')
                        : ''}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Survey send history — every attempt with its date (up to 3) */}
              {(() => {
                const g = detailDialog.graduate!;
                const hist = Array.isArray(g.gradEmailHistory) ? g.gradEmailHistory : [];
                const STAGE_LABEL: Record<string, string> = {
                  initial: 'Initial',
                  reminder1: 'Reminder 1',
                  reminder2: 'Reminder 2',
                  manual: 'Manual resend',
                };
                const fmt = (d: string) => {
                  const [y, m, day] = (d || '').split('T')[0].split('-');
                  return (y && m && day) ? `${m}/${day}/${y}` : (d || '—');
                };
                if (hist.length > 0) {
                  return (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {/* gradEmailCount is the authoritative lifetime total; history entries
                          are deduped by (stage, date) so the array can be shorter. */}
                      Survey Send History ({g.gradEmailCount ?? hist.length} of {GRAD_MAX_EMAILS})
                      </Typography>
                      {[...hist].sort((a, b) => (a.attempt || 0) - (b.attempt || 0)).map((h, i) => (
                        <Typography key={i} variant="body2">
                          Attempt {h.attempt}: {fmt(h.date)} — {STAGE_LABEL[h.stage] || h.stage}
                        </Typography>
                      ))}
                    </Box>
                  );
                }
                if (g.lastGradEmailDate) {
                  return (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Survey Send History
                      </Typography>
                      <Typography variant="body2">
                        Last survey email sent: {fmt(g.lastGradEmailDate)}.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Per-attempt dates are recorded for sends going forward.
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}

              {(() => {
                const g = detailDialog.graduate!;
                const resends = Array.isArray(g.resendHistory) ? g.resendHistory : [];
                if (resends.length === 0) return null;
                const when = (iso: string) => {
                  const d = new Date(iso);
                  return isNaN(d.getTime()) ? (iso || '-') : d.toLocaleString();
                };
                return (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Resend History ({g.surveyResendCount ?? resends.length})
                    </Typography>
                    {resends.map((r, i) => (
                      <Box key={i} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          {when(r.at)} by {r.by || 'unknown'}
                          {r.capOverride ? ' (over the email cap)' : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.justification
                            ? `Reason: ${r.justification}`
                            : r.previousStatus === 'sent' || r.previousStatus === 'completed'
                              ? 'First resend - no justification required'
                              : 'First send - no justification required'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              {detailDialog.graduate.signedDocumentUrl && (
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadDocument(detailDialog.graduate!)}
                  >
                    Download Signed Document
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, graduate: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Graduate Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Graduate</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Student ID"
              value={newGraduate.studentId}
              onChange={(e) => setNewGraduate({ ...newGraduate, studentId: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Full Name"
              value={newGraduate.name}
              onChange={(e) => setNewGraduate({ ...newGraduate, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={newGraduate.email}
              onChange={(e) => setNewGraduate({ ...newGraduate, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Program"
              value={newGraduate.program}
              onChange={(e) => setNewGraduate({ ...newGraduate, program: e.target.value })}
              required
              fullWidth
              placeholder="e.g., Massage Therapy"
            />
            <TextField
              select
              label="Degree Level"
              value={newGraduate.degreeLevel}
              onChange={(e) => setNewGraduate({ ...newGraduate, degreeLevel: e.target.value })}
              required
              fullWidth
            >
              <MenuItem value="Associate">Associate</MenuItem>
              <MenuItem value="Bachelor">Bachelor</MenuItem>
              <MenuItem value="Certificate">Certificate</MenuItem>
              <MenuItem value="Diploma">Diploma</MenuItem>
            </TextField>
            <TextField
              label="Graduation Date"
              type="date"
              value={newGraduate.graduationDate}
              onChange={(e) => setNewGraduate({ ...newGraduate, graduationDate: e.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Time Zone"
              value={newGraduate.timezone}
              onChange={(e) => setNewGraduate({ ...newGraduate, timezone: e.target.value })}
              required
              fullWidth
              helperText="Emails will be sent at 9 AM and 5 PM in this time zone"
            >
              <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
              <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
              <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
              <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
              <MenuItem value="America/Phoenix">Arizona (MST - No DST)</MenuItem>
              <MenuItem value="America/Anchorage">Alaska Time (AKT)</MenuItem>
              <MenuItem value="Pacific/Honolulu">Hawaii Time (HST)</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddGraduate} variant="contained">
            Add Graduate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Graduate Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, graduate: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Graduate</DialogTitle>
        <DialogContent>
          {editDialog.graduate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Student ID"
                value={editDialog.graduate.studentId}
                onChange={(e) => setEditDialog({ 
                  ...editDialog, 
                  graduate: { ...editDialog.graduate!, studentId: e.target.value } 
                })}
                required
                fullWidth
              />
              <TextField
                label="Full Name"
                value={editDialog.graduate.name}
                onChange={(e) => setEditDialog({ 
                  ...editDialog, 
                  graduate: { ...editDialog.graduate!, name: e.target.value } 
                })}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={editDialog.graduate.email}
                onChange={(e) => setEditDialog({ 
                  ...editDialog, 
                  graduate: { ...editDialog.graduate!, email: e.target.value } 
                })}
                required
                fullWidth
              />
              <TextField
                label="Program"
                value={editDialog.graduate.program}
                onChange={(e) => setEditDialog({ 
                  ...editDialog, 
                  graduate: { ...editDialog.graduate!, program: e.target.value } 
                })}
                required
                fullWidth
                placeholder="e.g., Massage Therapy"
              />
              <TextField
                select
                label="Degree Level"
                value={editDialog.graduate.degreeLevel}
                onChange={(e) => setEditDialog({ 
                  ...editDialog, 
                  graduate: { ...editDialog.graduate!, degreeLevel: e.target.value } 
                })}
                required
                fullWidth
              >
                <MenuItem value="Associate">Associate</MenuItem>
                <MenuItem value="Bachelor">Bachelor</MenuItem>
                <MenuItem value="Certificate">Certificate</MenuItem>
                <MenuItem value="Diploma">Diploma</MenuItem>
              </TextField>
              <TextField
                label="Graduation Date"
                type="date"
                value={editDialog.graduate.graduationDate}
                onChange={(e) => setEditDialog({ 
                  ...editDialog, 
                  graduate: { ...editDialog.graduate!, graduationDate: e.target.value } 
                })}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, graduate: null })}>Cancel</Button>
          <Button onClick={handleEditGraduate} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialog}
        onClose={() => !bulkDeleting && setBulkDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete <strong>{selectedIds.length}</strong> graduate(s)?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
          {selectedIds.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Selected graduates:
              </Typography>
              <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                {selectedIds.map(id => {
                  const grad = graduates.find(g => g.id === id);
                  return grad ? (
                    <Chip 
                      key={id} 
                      label={`${grad.studentId} - ${grad.name}`} 
                      size="small" 
                      sx={{ m: 0.5 }} 
                    />
                  ) : null;
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)} disabled={bulkDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkDelete} 
            variant="contained" 
            color="error"
            disabled={bulkDeleting}
          >
            {bulkDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single-row status change menu */}
      <Menu
        anchorEl={statusMenu.anchor}
        open={Boolean(statusMenu.anchor)}
        onClose={() => setStatusMenu({ anchor: null, graduate: null })}
      >
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem
            key={opt}
            selected={statusMenu.graduate?.surveyStatus === opt}
            onClick={() => statusMenu.graduate && handleChangeStatus(statusMenu.graduate, opt)}
            sx={{ textTransform: 'capitalize' }}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>

      {/* Bulk status change menu (for multi-selected graduates) */}
      <Menu
        anchorEl={bulkStatusAnchor}
        open={Boolean(bulkStatusAnchor)}
        onClose={() => setBulkStatusAnchor(null)}
      >
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem
            key={opt}
            onClick={() => handleBulkChangeStatus(opt)}
            sx={{ textTransform: 'capitalize' }}
          >
            Set to {opt}
          </MenuItem>
        ))}
      </Menu>

      {/* Justification for a repeat resend */}
      <Dialog open={resendDialog.open} onClose={closeResendDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Resend survey to {resendDialog.graduate?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'var(--text-secondary)' }}>
            {(resendDialog.graduate?.gradEmailCount ?? 0) >= GRAD_MAX_EMAILS
              ? `This graduate has already received ${resendDialog.graduate?.gradEmailCount} of the `
                + `${GRAD_MAX_EMAILS} emails allowed in their lifetime.`
              : `This graduate has already been sent the survey again `
                + `${resendDialog.graduate?.surveyResendCount ?? 0} time(s).`}{' '}
            Record why it is being sent once more. This is kept with their record.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Justification"
            placeholder="e.g. Address bounced; graduate confirmed a new email by phone."
            value={resendDialog.justification}
            onChange={(e) =>
              setResendDialog({ ...resendDialog, justification: e.target.value, error: '' })
            }
            error={!!resendDialog.error}
            helperText={
              resendDialog.error ||
              `${resendDialog.justification.trim().length}/10 characters minimum`
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResendDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              resendDialog.justification.trim().length < 10 ||
              sendingSurvey === resendDialog.graduate?.id
            }
            onClick={() =>
              resendDialog.graduate &&
              handleSendSurvey(resendDialog.graduate, resendDialog.justification.trim())
            }
          >
            {sendingSurvey === resendDialog.graduate?.id ? 'Sending\u2026' : 'Resend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Relabel release-only (M5): the dry run's counts, including the email it would lead to, then
          one confirm. Nothing is written until the confirm button is pressed. */}
      <Dialog
        open={relabelPreview !== null}
        onClose={() => !relabelBusy && setRelabelPreview(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Relabel release-only completions</DialogTitle>
        <DialogContent>
          {relabelPreview && (() => {
            const p = relabelPreview;
            const r = p.reminders;
            const keptTotal = p.kept.responseLinked + p.kept.responseUnlinkedSameEmail
              + p.kept.responseViaDeletedRecord + p.kept.neverEmailed + p.kept.relabelledBefore;
            const why = (reason: string) => {
              const pre = /^pre-(\d{4})-manual-only$/.exec(reason);
              if (pre) return `graduated before ${pre[1]}`;
              return ({
                'reminder-suppressed': 'opted out',
                'cap-reached': `already had ${GRAD_MAX_EMAILS} emails`,
                'no-graduation-year': 'no graduation date',
                'catchup-cohort': 'left to the catch-up timer',
              } as Record<string, string>)[reason] || reason;
            };
            const dates = Object.entries(r.byDate).map(([d, n]) => `${n} on ${d}`).join(', ');
            const notEligible = Object.entries(r.notEligible).map(([k, n]) => `${n} ${why(k)}`).join(', ');
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                <Typography variant="body2">
                  <strong>{p.toRelabel}</strong> graduate(s) are marked Completed only because they signed
                  the optional release form: no survey response is on their record. Relabelling returns
                  them to <strong>Sent</strong> (emailed, survey not answered) and keeps the old status on
                  each record. Email counts and send history are not changed.
                </Typography>
                {keptTotal > 0 && (
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                    Left as Completed: {p.kept.responseLinked} with a response that came back through
                    their survey link, {p.kept.responseUnlinkedSameEmail} with an unlinked response that
                    holds their email, {p.kept.responseViaDeletedRecord} with a response that came through
                    a survey link sent to their email for a graduate record since deleted,{' '}
                    {p.kept.neverEmailed} never emailed the survey, and{' '}
                    {p.kept.relabelledBefore} relabelled before and since set back by hand.
                  </Typography>
                )}
                {p.siblingHasResponse > 0 && (
                  <Typography variant="body2" sx={{ color: 'var(--warning)' }}>
                    {p.siblingHasResponse} of them answered the survey through their other degree's
                    record. A response counts for the record it came through, so they are included.
                  </Typography>
                )}
                <Alert severity={r.enabled && r.eligible > 0 ? 'warning' : 'info'}>
                  {r.enabled
                    ? `Automatic reminders are ON. ${r.eligible} of them would get one (recent graduates `
                      + `under the ${GRAD_MAX_EMAILS}-email cap)`
                    : `Automatic reminders are OFF, so none would go now. Turned on, ${r.eligible} of them `
                      + 'would get one'}
                  {r.eligible > 0 && dates ? `: ${dates}, at 8 AM their time.` : '.'}
                  {r.emails > 0
                    ? ` If none of them answers, that is ${r.emails} automatic email(s) in all, each at least`
                      + ` 30 days after the one before and none past the ${GRAD_MAX_EMAILS}-email cap.`
                    : ''}
                  {notEligible ? ` No automatic email for the rest: ${notEligible}.` : ''}
                  {r.wouldFlagAnomaly > 0
                    ? ` ${r.wouldFlagAnomaly} would be flagged as a possible spam anomaly: their send history`
                      + ' already shows two automatic emails under 30 days apart.'
                    : ''}
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRelabelPreview(null)} disabled={relabelBusy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRelabelConfirm}
            disabled={relabelBusy || !relabelPreview || relabelPreview.toRelabel === 0}
          >
            {relabelBusy ? 'Relabelling\u2026' : `Relabel ${relabelPreview?.toRelabel ?? 0}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
